/**
 * Profile Photo URL Helpers
 *
 * Utilities for generating signed URLs from storage paths stored in
 * profiles.profile_photo_url. These helpers handle the on-demand URL
 * generation pattern where storage paths are stored in the database
 * and signed URLs are generated at display time.
 *
 * Background:
 * - profiles.profile_photo_url now stores storage paths (e.g., "user-id/headshot/photo.jpg")
 * - Signed URLs expire, so we generate them on-demand when displaying
 * - Use batch functions for lists to minimize API calls
 *
 * @see Migration: 20260205110847_convert_profile_photo_url_to_storage_path.sql
 */
import type { SupabaseClient } from '@supabase/supabase-js';

import { getLogger } from '@kit/shared/logger';

import { resolveLocalCdnFallback } from './local-storage-cdn-fallback';
import { SignedUrlExpiry, StorageBuckets } from './storage-url.service';

/**
 * Server-side image resize applied at signing time via Supabase Storage's
 * render-transform endpoint (`/render/image/sign/...`).
 *
 * Supabase media (~67% of dancer covers, Meteor cutover) lives on Supabase
 * Storage behind a rotating signed-URL token, so it can NOT go through the
 * Cloudflare `cdn-cgi/image` transform (only stable R2 `cdn.ballee.co` URLs
 * can — see `cdn-image-url.service.ts` + `docs/architecture/image-rendering.md`).
 * Without a transform these serve full-size originals (covers up to ~2.6 MB
 * into a thumbnail card). Passing `transform` makes Supabase serve a resized,
 * format-negotiated (WebP/AVIF) variant — the in-architecture answer for
 * Supabase-hosted media. The transform is baked into the signed token, so the
 * batch sign endpoint can't carry it; the batch helper falls back to parallel
 * single-signs when a transform is requested.
 */
export interface ProfilePhotoTransform {
  width?: number;
  height?: number;
  quality?: number;
  resize?: 'cover' | 'contain' | 'fill';
}

/**
 * Default `resize` to `'contain'` when the caller didn't pick one.
 *
 * Supabase's render default is `'cover'`, and **cover with only one
 * dimension does NOT scale proportionally** — `{ width: 800 }` on a
 * 4016×6016 original serves an 800×6016 vertical STRIP (width cropped at
 * full original height). Cards then `object-cover` that sliver into a 3/4
 * box and show a giant scalp crop (dancer directory regression, 2026-06-12;
 * the strip also weighs ~800 KB, defeating the thumbnail transform).
 * `'contain'` always scales proportionally (800×1198 for the same file) and
 * leaves art-direction cropping to the CSS layer, which every consumer of
 * this helper already does. Callers that genuinely want a server-side crop
 * must pass BOTH dimensions AND `resize: 'cover'` explicitly.
 */
function withSafeResize(
  transform: ProfilePhotoTransform,
): ProfilePhotoTransform {
  return transform.resize ? transform : { ...transform, resize: 'contain' };
}

/**
 * Generate a signed URL from a profile photo storage path.
 *
 * Use this for single profile displays (e.g., profile page, chat message sender).
 *
 * @param client - Supabase client
 * @param storagePath - Storage path from profiles.profile_photo_url (e.g., "user-id/headshot/photo.jpg")
 * @returns Signed URL string or null if path is empty/invalid
 *
 * @example
 * ```typescript
 * const signedUrl = await getProfilePhotoSignedUrl(client, profile.profile_photo_url);
 * ```
 */
export async function getProfilePhotoSignedUrl(
  client: SupabaseClient,
  storagePath: string | null | undefined,
  transform?: ProfilePhotoTransform,
): Promise<string | null> {
  if (!storagePath || storagePath.trim() === '') {
    return null;
  }

  // Skip if already a full URL (legacy data or external URL) — these are
  // served as-is and can't be run through Supabase render-transform.
  if (storagePath.startsWith('http://') || storagePath.startsWith('https://')) {
    return storagePath;
  }

  // Local-dev prod-mirror: the synced DB carries storage paths whose binaries
  // only exist on the proxy target (prod Storage) — local signing 404s and
  // every avatar falls back to initials. Env-gated; null in production.
  const localFallback = resolveLocalCdnFallback(
    StorageBuckets.DANCER_MEDIA,
    storagePath,
  );
  if (localFallback) {
    return localFallback;
  }

  try {
    const { data, error } = await client.storage
      .from(StorageBuckets.DANCER_MEDIA)
      .createSignedUrl(storagePath, SignedUrlExpiry.IMMEDIATE_DISPLAY, {
        ...(transform ? { transform: withSafeResize(transform) } : {}),
      });

    if (error || !data?.signedUrl) {
      return null;
    }

    return data.signedUrl;
  } catch (error) {
    const logger = await getLogger();
    logger.warn(
      { storagePath, error },
      'Failed to generate signed URL for profile photo',
    );
    return null;
  }
}
// NOTE: kept as the dancer-media signing primitive (33+ private-tier call
// sites keep these names per WIP_simplifying_media_architecture). The
// custom ProfilePhotoTransform passthrough means delegation to
// mediaUrlSignedBatch (variant-keyed) would change the call contract —
// these remain the variant-free escape hatch; mediaUrlSigned* is the
// variant-keyed front door.

/**
 * Batch generate signed URLs for multiple profile photo storage paths.
 *
 * Use this for lists (e.g., chat participants, cast members, dancer lists).
 * More efficient than calling getProfilePhotoSignedUrl in a loop.
 *
 * @param client - Supabase client
 * @param storagePaths - Array of storage paths (nulls are preserved in output)
 * @param transform - Optional server-side resize. When supplied, each URL is
 *   signed individually (the batch sign endpoint can't carry a transform —
 *   it's baked into the signed token) in parallel. Omit for full-size /
 *   avatar-thumbnail callers to keep the single batched round-trip.
 * @returns Map of storage path -> signed URL (or null if generation failed)
 *
 * @example
 * ```typescript
 * const paths = dancers.map(d => d.profile_photo_url);
 * const urlMap = await batchGetProfilePhotoSignedUrls(client, paths, {
 *   width: 800,
 *   quality: 80,
 * });
 * const dancersWithUrls = dancers.map(d => ({
 *   ...d,
 *   avatarUrl: urlMap.get(d.profile_photo_url!) || null
 * }));
 * ```
 */
export async function batchGetProfilePhotoSignedUrls(
  client: SupabaseClient,
  storagePaths: (string | null | undefined)[],
  transform?: ProfilePhotoTransform,
): Promise<Map<string, string | null>> {
  const resultMap = new Map<string, string | null>();

  // Filter to valid storage paths (not empty, not already URLs)
  const validPaths = storagePaths.filter(
    (p): p is string =>
      !!p &&
      p.trim() !== '' &&
      !p.startsWith('http://') &&
      !p.startsWith('https://'),
  );

  // Handle legacy full URLs - pass through as-is
  storagePaths.forEach((p) => {
    if (p && (p.startsWith('http://') || p.startsWith('https://'))) {
      resultMap.set(p, p);
    }
  });

  // Local-dev prod-mirror fallback (see getProfilePhotoSignedUrl). Resolved
  // per-path BEFORE signing so the batch never hits the empty local bucket.
  const needSigning: string[] = [];
  for (const path of validPaths) {
    const localFallback = resolveLocalCdnFallback(
      StorageBuckets.DANCER_MEDIA,
      path,
    );
    if (localFallback) {
      resultMap.set(path, localFallback);
    } else {
      needSigning.push(path);
    }
  }

  if (needSigning.length === 0) {
    return resultMap;
  }

  // Transform requested: the batch sign endpoint can't carry a transform
  // (it's signed into the token), so sign each path individually in one
  // parallel wave. Bounded by page size; callers wrap this in a data cache.
  if (transform) {
    const signed = await Promise.all(
      needSigning.map((path) =>
        getProfilePhotoSignedUrl(client, path, transform),
      ),
    );
    needSigning.forEach((path, i) => resultMap.set(path, signed[i] ?? null));
    return resultMap;
  }

  try {
    const { data, error } = await client.storage
      .from(StorageBuckets.DANCER_MEDIA)
      .createSignedUrls(needSigning, SignedUrlExpiry.IMMEDIATE_DISPLAY);

    if (error || !data) {
      // On error, set all to null
      needSigning.forEach((path) => resultMap.set(path, null));
      return resultMap;
    }

    // Map results
    data.forEach((item, index) => {
      const path = needSigning[index];
      if (path) {
        resultMap.set(path, item.signedUrl || null);
      }
    });

    return resultMap;
  } catch (error) {
    const logger = await getLogger();
    logger.warn(
      { pathCount: needSigning.length, error },
      'Failed to batch generate signed URLs for profile photos',
    );
    // On exception, set all to null
    needSigning.forEach((path) => resultMap.set(path, null));
    return resultMap;
  }
}

/**
 * Enrich an array of items with profile photo signed URLs.
 *
 * Generic helper for adding avatar URLs to any array of objects.
 *
 * @param client - Supabase client
 * @param items - Array of items to enrich
 * @param pathExtractor - Function to get storage path from item
 * @param urlSetter - Function to create new item with URL added
 * @returns Array of enriched items
 *
 * @example
 * ```typescript
 * const dancersWithAvatars = await enrichWithProfilePhotoUrls(
 *   client,
 *   dancers,
 *   (d) => d.profile_photo_url,
 *   (d, url) => ({ ...d, avatarUrl: url })
 * );
 * ```
 */
/**
 * Participant profile shape used in chat services.
 * Defines the common structure for conversation/message participants.
 */
export interface ParticipantProfile {
  id: string;
  first_name: string | null;
  last_name: string | null;
  profile_photo_url?: string | null;
}

/**
 * Participant shape with optional profile.
 */
export interface ParticipantWithProfile {
  user_id: string;
  profile?: ParticipantProfile;
}

/**
 * Enrich participants with signed profile photo URLs.
 *
 * Use this for chat conversation/message participants.
 *
 * @param client - Supabase client
 * @param participants - Array of participants with profiles
 * @param signedUrlMap - Pre-computed signed URL map from batchGetProfilePhotoSignedUrls
 * @returns Participants with profile_photo_url converted to signed URLs
 *
 * @example
 * ```typescript
 * const paths = participants.flatMap(p => p.profile?.profile_photo_url ? [p.profile.profile_photo_url] : []);
 * const signedUrlMap = await batchGetProfilePhotoSignedUrls(client, paths);
 * const enriched = enrichParticipantsWithSignedUrls(participants, signedUrlMap);
 * ```
 */
export function enrichParticipantsWithSignedUrls<
  T extends ParticipantWithProfile,
>(participants: T[], signedUrlMap: Map<string, string | null>): T[] {
  return participants.map((p) => ({
    ...p,
    profile: p.profile
      ? {
          ...p.profile,
          profile_photo_url: p.profile.profile_photo_url
            ? (signedUrlMap.get(p.profile.profile_photo_url) ?? null)
            : null,
        }
      : p.profile,
  }));
}

/**
 * Collect profile photo paths from participants for batch URL generation.
 *
 * @param participants - Array of participants with profiles
 * @returns Array of non-null profile photo paths
 */
export function collectParticipantProfilePhotoPaths(
  participants: ParticipantWithProfile[],
): string[] {
  return participants
    .map((p) => p.profile?.profile_photo_url)
    .filter((p): p is string => !!p);
}

export async function enrichWithProfilePhotoUrls<T, R>(
  client: SupabaseClient,
  items: T[],
  pathExtractor: (item: T) => string | null | undefined,
  urlSetter: (item: T, url: string | null) => R,
): Promise<R[]> {
  if (items.length === 0) {
    return [];
  }

  // Extract all paths
  const paths = items.map(pathExtractor);

  // Get batch signed URLs
  const urlMap = await batchGetProfilePhotoSignedUrls(client, paths);

  // Apply URLs to items
  return items.map((item) => {
    const path = pathExtractor(item);
    const url = path ? (urlMap.get(path) ?? null) : null;
    return urlSetter(item, url);
  });
}
